import { useLoadFeatureAccessInsights } from '@ms/yammer-hooks/dist/featureAccessInsights';
import { useLoadMultiTenantOrganizationDetails } from '@ms/yammer-hooks/dist/multiTenantOrganizationDetails/useLoadMultiTenantOrganizationDetails';
import { useSendPresenceState } from '@ms/yammer-hooks/dist/userPresence';
import { useCurrentLocaleDirection } from '@ms/yammer-i18n-react';
import Skiplink from '@ms/yammer-web-components/dist/accessibility/Skiplink';
import AppContextProvider, { AppContextOptions } from '@ms/yammer-web-components/dist/app/AppContext';
import AppDocumentHeadOverrides from '@ms/yammer-web-components/dist/app/AppDocumentHeadOverrides';
import FavoritesLimitReachedDialog from '@ms/yammer-web-components/dist/group/FavoritesLimitReachedDialog';
import { LinkRenderingOptions, LinkRenderingOptionsProvider } from '@ms/yammer-web-components/dist/link/YammerLink';
import MediaLightbox from '@ms/yammer-web-components/dist/messageAttachments/MediaLightbox';
import RealtimeClient from '@ms/yammer-web-components/dist/realtime/RealtimeClient';
import { NotificationsCountSubscription } from '@ms/yammer-web-components/dist/realtime/subscriptions';
import SessionContextProviderWithHooks from '@ms/yammer-web-components/dist/session/SessionContextProviderWithHooks';
import UsagePolicyDialog from '@ms/yammer-web-components/dist/usagePolicy/UsagePolicyDialog';
import AppContainer from '@ms/yammer-web-core-ui/dist/components/AppContainer';
import { PageLayoutContentMainAreaAriaRole } from '@ms/yammer-web-core-ui/dist/components/PageLayout';
import { usePrepareIcons } from '@ms/yammer-web-core-ui/dist/util/icons';
import { UIFeaturesProvider } from '@ms/yammer-web-ui-features/dist/features';
import TeamsClientProvider from 'components/TeamsClientProvider';
import { useDeepLinkPath, useIsMobileClient, useIsTeamsContrastTheme, useIsTeamsTab } from 'hooks/selectors';
import { useAppFeatureSettings } from 'hooks/useAppFeatureSettings';
import { useAppThemes } from 'hooks/useAppThemes';
import { useHandleOnLoadCachedApp } from 'hooks/useHandleOnLoadCachedApp';
import { useIsVivaEngageApp } from 'hooks/useIsVivaEngageApp';
import { useLoadUnseenNotifications } from 'hooks/useLoadUnseenNotifications';
import { useReportNotificationsCountEffect } from 'hooks/useReportNotificationsCountEffect';
import React, { FC, useMemo } from 'react';
import { isInVivaConnectionsApp } from 'vivaConnections';

import Routes from '../Routes';

import { useReportVivaEngageAppTreatment } from './hooks/useReportVivaEngageAppTreatment';

const App: FC = () => {
  const themes = useAppThemes();
  const isVivaEngageApp = useIsVivaEngageApp();
  const isMobileClient = useIsMobileClient();
  const isTeamsTab = useIsTeamsTab();
  const isTeamsContrastTheme = useIsTeamsContrastTheme();
  const features = useAppFeatureSettings();
  const dir = useCurrentLocaleDirection();

  const deepLinkPath = useDeepLinkPath();
  const hasDeepLinkPath = !!deepLinkPath;

  useLoadUnseenNotifications();
  useReportNotificationsCountEffect();
  useHandleOnLoadCachedApp();
  useSendPresenceState();
  useLoadMultiTenantOrganizationDetails();
  useLoadFeatureAccessInsights({ vivaEngagePremiumOnly: true });

  useReportVivaEngageAppTreatment();

  const isInVivaConnections = isInVivaConnectionsApp();
  usePrepareIcons();

  const teamsClientContextOptions = useMemo(
    () => ({
      collapseContentSidebar: false,
      shouldHideHeaders: isTeamsTab || isInVivaConnections,
      shouldHideLeftSidebar: isTeamsTab,
      shouldHideSubHeader: isTeamsTab || isInVivaConnections,
      shouldShowStaticAppHeader: false,
    }),
    [isTeamsTab, isInVivaConnections]
  );

  const appContextOptions: Partial<AppContextOptions> = useMemo(
    () => ({
      enableCollapsiblePanelNav: isVivaEngageApp || isInVivaConnections,
      enableCommunityDiscoveryV2: isVivaEngageApp,
    }),
    [isVivaEngageApp, isInVivaConnections]
  );

  const linkRenderingOptions: LinkRenderingOptions = useMemo(
    () => ({
      disableLivePersonaCard: isMobileClient,
      shouldHideDownloadAttachmentLink: isMobileClient,
      shouldHideLegacyPageLinks: isMobileClient,
      shouldHideDescriptionMoreInfoLink: isMobileClient,
      shouldOpenDiscoverCommunitiesLinkInNewWindow: false,
      shouldOpenGroupHeaderSettingsLinkInNewWindow: true,
      shouldOpenGroupInfoLinkInNewWindow: true,
      shouldOpenGroupFilesInLegacyPage: true,
      shouldOpenGroupFilesInNewWindow: true,
      shouldOpenGroupDeleteInNewWindow: true,
      isThreadStarterCopyButtonLinkExternal: true,
      shouldOpenEventLinkInNewWindow: isTeamsTab,
      shouldOpenGroupLinkInNewWindow: isTeamsTab,
      shouldOpenThreadLinkInNewWindow: isTeamsTab,
      shouldOpenTagLinkInNewWindow: isTeamsTab,
      shouldOpenViewConversationLinkInNewWindow: isTeamsTab,
      shouldOpenYammerLinkInNewWindow: isTeamsTab,
    }),
    [isMobileClient, isTeamsTab]
  );

  return (
    <AppContainer
      theme={themes.fluent8}
      fluent9Theme={themes.fluent9}
      emulateHighContrast={isTeamsContrastTheme}
      dir={dir}
    >
      <AppContextProvider mainAreaAriaRole={PageLayoutContentMainAreaAriaRole.Region} options={appContextOptions}>
        <UIFeaturesProvider features={features}>
          <SessionContextProviderWithHooks>
            <Skiplink />
            <AppDocumentHeadOverrides />
            <TeamsClientProvider options={teamsClientContextOptions}>
              <LinkRenderingOptionsProvider options={linkRenderingOptions}>
                <Routes />
                <UsagePolicyDialog />
                <FavoritesLimitReachedDialog />
                <MediaLightbox />
              </LinkRenderingOptionsProvider>
            </TeamsClientProvider>
            <NotificationsCountSubscription />
            <RealtimeClient isRealtimeDisabled={hasDeepLinkPath} />
          </SessionContextProviderWithHooks>
        </UIFeaturesProvider>
      </AppContextProvider>
    </AppContainer>
  );
};

export default App;
